import {
  Alert,
  Box,
  Chip,
  Collapse,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import {
  CLASSIFICATION,
  classifyTerms,
  settledAgreedFor,
  settlementTotals,
} from "./withdrawalSettlementMath";

const MODES = [
  { value: "COLLECT_FULL", label: "Collect in full" },
  { value: "WAIVE_REMAINING", label: "Waive remaining" },
  { value: "CUSTOM", label: "Custom settled amount" },
];

const money = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const rangeLabel = (affected) => {
  if (affected.length === 1) return `T${affected[0].termNumber}`;
  return `T${affected[0].termNumber} to T${affected[affected.length - 1].termNumber}`;
};

/**
 * Controlled settlement editor. `value` is the request payload; every edit emits a whole new
 * payload through `onChange` rather than mutating, so the parent owns the state and the same panel
 * serves both the withdraw dialog and the Direct Account editor.
 *
 * `canAdjustFinancials` splits the two jobs the panel does. Everyone settling a withdrawal picks
 * the term and how the student's balance is closed; only the accounts team decides what commission
 * and bonus survive it. Defaults to false, and the server redacts the same figures — a panel
 * without the right is not hiding numbers it received, it never received them.
 */
const WithdrawalSettlementPanel = ({
  terms = [],
  value,
  onChange,
  readOnly = false,
  canAdjustFinancials = false,
}) => {
  const [showRetention, setShowRetention] = useState(false);

  const withdrawalTermUuid = value?.withdrawalTermUuid ?? null;
  const mode = value?.settlementMode ?? "COLLECT_FULL";
  const customAmount = value?.settledAgreed ?? "";
  const retention = useMemo(() => {
    const map = {};
    (value?.retention || []).forEach((entry) => {
      map[entry.termUuid] = entry;
    });
    return map;
  }, [value]);

  const classified = useMemo(
    () => classifyTerms(terms, withdrawalTermUuid),
    [terms, withdrawalTermUuid]
  );
  const settlementTerm = classified.find((t) => t.classification === CLASSIFICATION.SETTLEMENT);
  const affected = classified.filter((t) => t.classification !== CLASSIFICATION.UNAFFECTED);
  const totals = settlementTotals(terms, withdrawalTermUuid, mode, customAmount, retention);

  const emit = (patch) => onChange?.({ ...value, ...patch });

  const setRetention = (termUuid, key, checked) => {
    const next = affected.map((term) => ({
      termUuid: term.termUuid,
      retainCommission: retention[term.termUuid]?.retainCommission ?? false,
      retainBonus: retention[term.termUuid]?.retainBonus ?? false,
      ...(term.termUuid === termUuid ? { [key]: checked } : {}),
    }));
    emit({ retention: next });
  };

  const sentToProviderAndCancelled = affected.filter(
    (term) => term.classification === CLASSIFICATION.CANCELLED && term.sentToProvider
  );

  return (
    <Box>
      <TextField
        select
        size="small"
        fullWidth
        label="Student withdrew during"
        value={withdrawalTermUuid ?? ""}
        disabled={readOnly}
        onChange={(e) => emit({ withdrawalTermUuid: e.target.value })}
        sx={{ mb: 2 }}
      >
        {terms.map((term) => (
          <MenuItem key={term.termUuid} value={term.termUuid}>
            {`Term T${term.termNumber}`}
            {term.admissionDate ? ` (${term.admissionDate})` : ""}
          </MenuItem>
        ))}
      </TextField>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Term</TableCell>
            <TableCell align="right">Agreed</TableCell>
            <TableCell align="right">Paid</TableCell>
            <TableCell align="right">Due</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {classified.map((term) => {
            const cancelled = term.classification === CLASSIFICATION.CANCELLED;
            return (
              <TableRow key={term.termUuid} sx={cancelled ? { opacity: 0.55 } : undefined}>
                <TableCell>{`T${term.termNumber}`}</TableCell>
                <TableCell
                  align="right"
                  sx={cancelled ? { textDecoration: "line-through" } : undefined}
                >
                  {money(term.agreed)}
                </TableCell>
                <TableCell align="right">{money(term.netPaid)}</TableCell>
                <TableCell
                  align="right"
                  sx={cancelled ? { textDecoration: "line-through" } : undefined}
                >
                  {money(Math.max(0, term.agreed - term.netPaid))}
                </TableCell>
                <TableCell>
                  {term.classification === CLASSIFICATION.SETTLEMENT && (
                    <Chip size="small" color="warning" label="Settlement" />
                  )}
                  {cancelled && <Chip size="small" label="Cancelled" />}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {settlementTerm && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {`How is T${settlementTerm.termNumber} settled?`}
          </Typography>
          <RadioGroup value={mode} onChange={(e) => emit({ settlementMode: e.target.value })}>
            {MODES.map((option) => {
              const settled = settledAgreedFor(option.value, settlementTerm, customAmount);
              const delta = settled - settlementTerm.netPaid;
              return (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  disabled={readOnly}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      {option.label}
                      <Typography component="span" variant="caption" color="text.secondary">
                        {option.value === "CUSTOM"
                          ? ""
                          : delta > 0
                          ? ` (${money(delta)} still due)`
                          : delta < 0
                          ? ` (${money(Math.abs(delta))} to refund)`
                          : " (nothing outstanding)"}
                      </Typography>
                    </Typography>
                  }
                />
              );
            })}
          </RadioGroup>

          {mode === "CUSTOM" && (
            <TextField
              size="small"
              type="number"
              label="Settled amount"
              value={customAmount}
              disabled={readOnly}
              onChange={(e) => emit({ settledAgreed: e.target.value })}
              helperText={
                totals.refundOwed > 0
                  ? `${money(totals.refundOwed)} to refund`
                  : `${money(totals.dueAfter)} still to collect`
              }
              sx={{ mt: 1, maxWidth: 260 }}
            />
          )}
        </Box>
      )}

      {sentToProviderAndCancelled.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {`${rangeLabel(
            sentToProviderAndCancelled
          )} already went to the provider. Cancelling here does` +
            ` not withdraw that invoice; unwind it with the provider separately.`}
        </Alert>
      )}

      {canAdjustFinancials && affected.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {`Commission and bonus for ${rangeLabel(affected)} will not be counted (${money(
                totals.profitLossImpact
              )} P/L)`}
            </Typography>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: "pointer" }}
              onClick={() => setShowRetention((open) => !open)}
            >
              {showRetention ? "Hide" : "Adjust"}
            </Typography>
          </Stack>
          <Collapse in={showRetention}>
            <Stack sx={{ mt: 1 }} spacing={0.5}>
              {affected.map((term) => (
                <Stack key={term.termUuid} direction="row" spacing={2} alignItems="center">
                  <Typography variant="caption" sx={{ minWidth: 32 }}>
                    {`T${term.termNumber}`}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        disabled={readOnly}
                        checked={Boolean(retention[term.termUuid]?.retainCommission)}
                        onChange={(e) =>
                          setRetention(term.termUuid, "retainCommission", e.target.checked)
                        }
                      />
                    }
                    label={`Retain commission ${money(term.commission)}`}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        disabled={readOnly}
                        checked={Boolean(retention[term.termUuid]?.retainBonus)}
                        onChange={(e) =>
                          setRetention(term.termUuid, "retainBonus", e.target.checked)
                        }
                      />
                    }
                    label={`Retain bonus ${money(term.bonus)}`}
                  />
                </Stack>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        {`Due after withdrawal ${money(totals.dueAfter)} · Refund owed ${money(
          totals.refundOwed
        )}` + (canAdjustFinancials ? ` · P/L impact ${money(totals.profitLossImpact)}` : "")}
      </Alert>
    </Box>
  );
};

export default WithdrawalSettlementPanel;
